import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
var _excluded = ["iconOnly", "align", "as", "children", "variant", "size", "icon", "iconRight", "iconScale", "fullWidth", "title", "textOverflow", "isLoading", "isDisabled", "aria-label", "loadingLabel"];
var _templateObject;
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { alpha, mix, getSolidBackgroundShade, isDark, pxToRem, _3px } from 'utils/ui';
import { fillParent, ellipsis, overflowWrap } from 'styles/mixins';
import { alignMap } from 'styles/styleProps/flexProps';
import { getPropFilter, getPropNamesFromPropDefinition } from 'utils/ui/styleProps';
import positionProps, { positionPropsDef } from 'styles/styleProps/positionProps';
import marginProps, { marginPropsDef } from 'styles/styleProps/marginProps';
import ButtonCore from 'components/ButtonCore';
import Icon from 'components/Icon';
import { SpinnerDot } from 'components/icons';
import VisuallyHidden from 'components/VisuallyHidden';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var stylePropNames = getPropNamesFromPropDefinition([].concat(_toConsumableArray(positionPropsDef), _toConsumableArray(marginPropsDef)));
var shouldForwardProp = getPropFilter(['round', 'square', 'fullWidth', 'isLoading', 'variant', 'size', 'align'].concat(_toConsumableArray(stylePropNames)));
var BUTTON_HEIGHT_MAP = {
  small: 25,
  medium: 33,
  default: 42,
  large: 56
};
var Wrapper = styled(ButtonCore).withConfig({
  shouldForwardProp: shouldForwardProp
})(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\t/* Structure, size & spacing */\n\n\t", "\n\tdisplay: inline-flex;\n\talign-items: center;\n\tjustify-content: ", ";\n\n\t", "\n\tmax-width: 100%;\n\t", "\n\n\tfont-size: ", ";\n\tfont-weight: 600;\n\tline-height: 1.125;\n\ttext-align: ", ";\n\ttext-decoration: none;\n\n\tmin-height: ", ";\n\tpadding-inline: ", ";\n\n\t", "\n\n\t", "\n\n\t", "\n\n\t", "\n\n\t/* Colours */\n\n\t", "\n\n\t", "\n\n\t", "\n\n\t", "\n\n\t/* Add a border to default button */\n\t", "\n\n\t&.focus-visible {\n\t\t/* Override the default focus indicator of ButtonCore\n\t\t * Actual focus styles are applied using the FocusRing\n\t\t * component further below */\n\t\toutline: none;\n\t}\n\n\t&.is-disabled {\n\t\t", "\n\n\t\tcursor: ", ";\n\n\t\t", "\n\t}\n"])), positionProps, function (p) {
  return alignMap[p.align || 'center'];
}, function (p) {
  return p.fullWidth && css(["display:flex;width:100%;"]);
}, marginProps, function (p) {
  return p.theme.globals.typeScale.m;
}, function (p) {
  return p.align;
}, function (p) {
  return pxToRem(BUTTON_HEIGHT_MAP[p.size || 'default']);
}, pxToRem(12), function (p) {
  return p.size === 'small' && css(["padding-inline:", ";font-size:", ";"], pxToRem(5), function (p) {
    return p.theme.globals.typeScale.xs;
  });
}, function (p) {
  return p.size === 'medium' && css(["padding-inline:", ";font-size:", ";"], pxToRem(8), function (p) {
    return p.theme.globals.typeScale.s;
  });
}, function (p) {
  return p.size === 'large' && css(["padding-inline:", ";"], pxToRem(19));
}, function (p) {
  return !p.square && css(["border-radius:", ";"], p.round ? '2rem' : pxToRem(p.theme.globals.borderRadius.buttons));
}, function (p) {
  return (p.variant === 'default' || p.variant === 'primary' || p.variant === 'important') && css(["color:", ";background-color:", ";"], function (p) {
    return p.theme.globals.buttons[p.variant].text;
  }, function (p) {
    return p.theme.globals.buttons[p.variant].background;
  });
}, function (p) {
  return p.variant === 'default-negative' && css(["color:", ";background-color:", ";"], function (p) {
    return p.theme.globals.colorBlocks.negative;
  }, function (p) {
    return p.theme.globals.buttons.default.background;
  });
}, function (p) {
  return p.variant === 'transparent' && css(["color:", ";background-color:transparent;"], function (p) {
    return p.theme.links;
  });
}, function (p) {
  return p.variant === 'shaded' && css(["color:", ";background-color:", ";"], function (p) {
    return p.theme.links;
  }, function (p) {
    return alpha(p.theme.shade, p.theme.shadeStrength);
  });
}, function (p) {
  return (p.variant === 'default' || p.variant === 'default-negative') && css(["&:not(.is-disabled)::before{content:'';", " border-radius:inherit;border:1px solid ", ";}"], fillParent, function (p) {
    return alpha(p.theme.shade, p.theme.lineStrength);
  });
}, function (p) {
  var darkColor = mix(p.theme.text)('white', p.theme.textDimStrength * 0.75);
  var lightColor = getSolidBackgroundShade(p.theme);
  if (p.variant === 'primary' || p.variant === 'important') {
    var isTextDark = isDark(p.theme.globals.buttons[p.variant].text);
    return "\n\t\t\t\tcolor: ".concat(isTextDark ? darkColor : lightColor, ";\n\t\t\t\tbackground-color: ").concat(isTextDark ? lightColor : darkColor, ";\n\t\t\t\t");
  } else {
    return "\n\t\t\t\tcolor: ".concat(darkColor, ";\n\t\t\t\tbackground-color: ").concat(lightColor, ";\n\t\t\t\t");
  }
}, function (p) {
  return p.isLoading ? 'wait' : 'not-allowed';
}, function (p) {
  return (p.variant === 'transparent' || p.variant === 'shaded') && css(["background-color:transparent;border-color:transparent;"]);
});
var FocusRing = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return prop !== 'variant';
  }
}).withConfig({
  displayName: "Button__FocusRing",
  componentId: "sc-cuikcm-0"
})(["", " border-radius:inherit;box-shadow:0 0 0 ", " ", ";pointer-events:none;opacity:0;transition:opacity 250ms linear;will-change:opacity;", ".focus-visible > &&{top:-", ";left:-", ";bottom:-", ";right:-", ";box-shadow:0 0 0 ", " ", ";opacity:1;transition-duration:50ms;}", ":not(.is-disabled):active > &&{opacity:1;transition-duration:50ms;}"], fillParent, _3px, function (p) {
  return alpha(p.theme.shade, p.theme.lineStrength + 0.05);
}, Wrapper, _3px, _3px, _3px, _3px, _3px, function (p) {
  return p.variant === 'primary' || p.variant === 'important' ? p.theme.globals.buttons[p.variant].background : p.theme.links;
}, Wrapper);
var HoverShade = styled.span.withConfig({
  displayName: "Button__HoverShade",
  componentId: "sc-cuikcm-1"
})(["", " border-radius:inherit;background-color:", ";pointer-events:none;opacity:0;transition:opacity 250ms linear;will-change:opacity;", ".is-active > &{opacity:0.666;box-shadow:inset 0 0 0.25rem rgba(0,0,0,0.5);}", ":not(.is-disabled):hover > &&{opacity:1;transition-duration:50ms;}", ":not(.is-disabled):active > &&{opacity:0;transition-duration:250ms;}"], fillParent, function (p) {
  return alpha(p.theme.shade, Math.min(p.theme.shadeStrength, 0.12));
}, Wrapper, Wrapper, Wrapper);

/**
 * The button label should just have some horizontal spacing.
 * But if `textOverflow="ellipsis" is set, the Button label
 * will have overflow set to "hidden", which can cause the
 * descenders of some characters (g, y, j) to be cut off.
 * To avoid this, we also give the text label some vertical
 * padding and visually remove it using negative margins.
 */

var ButtonText = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['textOverflow', 'px'].includes(prop);
  }
}).withConfig({
  displayName: "Button__ButtonText",
  componentId: "sc-cuikcm-2"
})(["padding:", " ", ";vertical-align:middle;", " z-index:0;"], function (p) {
  return p.theme.globals.spacing.xxs;
}, function (p) {
  return p.theme.globals.spacing[p.px];
}, function (p) {
  return p.textOverflow === 'ellipsis' ? ellipsis : overflowWrap;
});
var ButtonIcon = function ButtonIcon(_ref) {
  var isLoading = _ref.isLoading,
    icon = _ref.icon,
    iconScale = _ref.iconScale;
  if (isLoading) {
    return /*#__PURE__*/_jsx(SpinnerDot, {
      disablePointerEvents: true,
      scale: iconScale
    });
  }
  if (typeof icon === 'string') {
    return /*#__PURE__*/_jsx(Icon, {
      disablePointerEvents: true,
      name: icon,
      scale: iconScale
    });
  } else {
    return icon;
  }
};
export var Button = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var iconOnly = _ref2.iconOnly,
    _ref2$align = _ref2.align,
    align = _ref2$align === void 0 ? 'center' : _ref2$align,
    as = _ref2.as,
    children = _ref2.children,
    _ref2$variant = _ref2.variant,
    variant = _ref2$variant === void 0 ? 'default' : _ref2$variant,
    size = _ref2.size,
    icon = _ref2.icon,
    iconRight = _ref2.iconRight,
    iconScale = _ref2.iconScale,
    fullWidth = _ref2.fullWidth,
    title = _ref2.title,
    _ref2$textOverflow = _ref2.textOverflow,
    textOverflow = _ref2$textOverflow === void 0 ? 'wrap' : _ref2$textOverflow,
    isLoading = _ref2.isLoading,
    isDisabled = _ref2.isDisabled,
    ariaLabel = _ref2['aria-label'],
    loadingLabelProp = _ref2.loadingLabel,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var Component = as || 'button';
  var loadingLabel = loadingLabelProp || t('loading');
  return (
    /*#__PURE__*/
    /*
     Warning!!! `Wrapper` is currently not being properly typechecked.
     Comment out `otherProps` and `as` when making changes to check types 
    */
    _jsxs(Wrapper, _objectSpread(_objectSpread({
      ref: ref,
      forwardedAs: Component,
      position: "relative",
      "aria-label": ariaLabel || title,
      variant: variant,
      size: size,
      align: align,
      fullWidth: fullWidth
    }, otherProps), {}, {
      "aria-live": isLoading !== undefined ? 'polite' : undefined,
      isDisabled: isDisabled || isLoading,
      isLoading: isLoading,
      children: [/*#__PURE__*/_jsx(HoverShade, {}), /*#__PURE__*/_jsx(FocusRing, {
        variant: variant
      }), icon && /*#__PURE__*/_jsx(ButtonIcon, {
        isLoading: isLoading && (!iconRight || iconOnly),
        icon: icon,
        iconScale: iconScale
      }), children && (iconOnly ? /*#__PURE__*/_jsxs(VisuallyHidden, {
        children: [children, isLoading && loadingLabel]
      }) : /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(ButtonText, {
          textOverflow: textOverflow,
          px: size === 'large' ? 'xs' : 'xxs',
          children: children
        }), isLoading && /*#__PURE__*/_jsx(VisuallyHidden, {
          children: loadingLabel
        }), !icon && !iconRight && isLoading && /*#__PURE__*/_jsx(SpinnerDot, {
          disablePointerEvents: true,
          scale: iconScale
        })]
      })), iconRight && /*#__PURE__*/_jsx(ButtonIcon, {
        isLoading: isLoading,
        icon: iconRight,
        iconScale: iconScale
      })]
    }))
  );
});
Button.displayName = 'Button';
export default Button;